import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import categoryBoys from '@/assets/category-boys.jpg';
import categoryGirls from '@/assets/category-girls.jpg';
import categoryLadies from '@/assets/category-ladies.jpg';
import categoryMen from '@/assets/category-men.jpg';

const categories = [
  { 
    name: 'Men', 
    tagline: 'Sharp & Refined',
    image: categoryMen, 
    link: '/category/men',
    items: '1,200+'
  },
  { 
    name: 'Ladies', 
    tagline: 'Elegant & Chic',
    image: categoryLadies, 
    link: '/category/ladies',
    items: '2,500+'
  },
  { 
    name: 'Boys', 
    tagline: 'Cool & Playful',
    image: categoryBoys, 
    link: '/category/boys',
    items: '800+'
  },
  { 
    name: 'Girls', 
    tagline: 'Sweet & Stylish',
    image: categoryGirls, 
    link: '/category/girls',
    items: '950+'
  },
];

export function CategoryShowcase() {
  return (
    <section id="categories" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block text-primary font-medium text-sm uppercase tracking-wider mb-3 font-sans">
            Shop by Category
          </span>
          <h2 className="section-heading font-serif">
            Find Your Perfect Fit
          </h2>
          <p className="section-subheading max-w-md mx-auto font-sans">
            Explore our curated collections designed for every member of your family
          </p>
        </motion.div>

        {/* Category Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {categories.map((category, index) => (
            <motion.div
              key={category.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={category.link} className="group block">
                <div className="relative aspect-[3/4] rounded-2xl md:rounded-3xl overflow-hidden bg-secondary">
                  {/* Image */}
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />

                  {/* Content */}
                  <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-end">
                    <div className="transform transition-transform duration-300 group-hover:translate-y-0 translate-y-2">
                      <p className="text-primary-foreground/70 text-xs md:text-sm mb-1 font-sans">
                        {category.tagline}
                      </p>
                      <h3 className="text-primary-foreground text-xl md:text-2xl font-semibold mb-2 font-serif">
                        {category.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="text-primary-foreground/80 text-xs md:text-sm font-sans">
                          {category.items} items
                        </span>
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary-foreground/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                          <ArrowRight className="h-4 w-4 md:h-5 md:w-5 text-primary-foreground transition-transform duration-300 group-hover:translate-x-0.5" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hover Border Effect */}
                  <div className="absolute inset-0 rounded-2xl md:rounded-3xl border-2 border-transparent group-hover:border-primary/30 transition-colors duration-300" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}